"use client";
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import "./video.css";
import { VideoServices } from "./VideoServices";
import Image from "next/image";

interface ServiceItem {
  id: number;
  cid: number;
  name: string;
  subHeading: string;
  amount: number;
  mrp: number;
  discount: number;
  coverImage: { secureHttpPath: string };
}

interface VideoBanner {
  bannerFile?: {
    secureHttpPath?: string;
  };
  videoFile?: {
    secureHttpPath?: string;
  };
  completeVideo?: {
    secureHttpPath?: string;
  };
  servicesList: ServiceItem[];
}
interface webImage {
  webImage: { secureHttpPath: string };
}
interface VideoSliderProps {
  videoBanners: any[];
  videoBanner: webImage;
}

const VideoSlider: React.FC<VideoSliderProps> = ({
  videoBanners,
  videoBanner,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [completeVideo, setCompleteVideo] = useState<VideoBanner | null>();
  const [cid, setCid] = useState<number>(0);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleDialogOpen = (item: any) => {
    setCompleteVideo(item);
    setOpenDialog(true);
    setCid(item?.categoryId);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setCompleteVideo(null);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const videoElement = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            requestAnimationFrame(() => {
              videoElement
                .play()
                .catch((err) => console.warn("Video play error:", err));
            });
          } else {
            videoElement.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videoRefs.current.forEach((video) => {
      if (video) observer.observe(video);
    });

    return () => observer.disconnect();
  }, [videoBanners]);

  return (
    <Box
      sx={{
        background: {
          xs: "#fff",
          sm: "linear-gradient(45deg, #fefac4, #ffef5e)",
        },
        marginTop: { xs: "1rem", sm: "3rem" },
        position: "relative",
        overflow: "hidden",
        zIndex: 0,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: -350,
          bottom: 70,
          border: "20px solid #93ec95",
          width: "800px",
          height: "800px",
          borderRadius: "50%",
          zIndex: 1,
        }}
      ></Box>
      <Box
        sx={{
          display: isSmallScreen ? "block" : "none",
          maxHeight: "400px",
          position: "absolute",
          overflow: "hidden",
          height: "400px",
        }}
      >
        <Image
          src={videoBanner?.webImage?.secureHttpPath}
          width={1000}
          height={1000}
          style={{
            objectFit: "contain",
            width: "auto",
            height: "-webkit-fit-available",
            zIndex: 0,
          }}
          alt="Video_Banner"
        />
      </Box>
      <Grid
        container
        item
        xs={11.5}
        sm={11}
        md={9.5}
        lg={9.5}
        margin="auto"
        sx={{
          display: { xs: "none", sm: "block" },
          position: "relative",
          zIndex: 11,
        }}
      >
        <Box
          sx={{
            paddingTop: "1rem",
            width: "100%",
            textAlign: "left",
          }}
          className="video-heading"
        >
          <Typography
            variant="h6"
            sx={{
              color: "#6d123f",
              fontWeight: 600,
              fontSize: { xs: "16px", sm: "24px" },
            }}
          >
            Experience Effortless Services
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#6d123f", fontSize: { xs: "12px", sm: "18px" } }}
          >
            Your Personal Salon, available anytime, anywhere
          </Typography>
        </Box>
      </Grid>
      <Box
        sx={{
          padding: { xs: "5.5rem 0px 2rem", sm: "1rem 0px 2rem" },
          display: "flex",
          justifyContent: {
            xs: "start",
            sm: "start",
            md: "start",
            lg: "start",
            xl: "center",
          },
          alignItems: "center",
          overflowX: "scroll",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {videoBanners?.map((item, index) => (
          <Box
            key={index}
            sx={{
              margin: { xs: "auto 0.5rem", sm: "auto 1rem" },
              boxShadow: "1px 1px 4px 1px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              width: { xs: "280px", sm: "350px" },
              minWidth: { xs: "280px", sm: "350px" },
              height: { xs: "280px", sm: "350px" },
              zIndex: 10,
            }}
          >
            <video
              id="myVideo"
              ref={(el) => (videoRefs.current[index] = el)}
              autoPlay
              muted
              loop
              onClick={() => handleDialogOpen(item)}
              onContextMenu={(e) => e.preventDefault()}
              disablePictureInPicture
              controlsList="nodownload noremoteplayback noplaybackrate"
              controls={false} // Disable video controls
              playsInline
              style={{
                borderRadius: "8px",
                width: isSmallScreen ? "280px" : "100%",
                minWidth: isSmallScreen ? "280px" : "100%",
                height: isSmallScreen ? "280px" : "100%",
                objectFit: "cover",
                cursor: "pointer",
              }}
            >
              <source src={item?.cover?.secureHttpPath} type="video/mp4" />
            </video>
          </Box>
        ))}
      </Box>
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="xs"
        aria-labelledby="video-dialog-title" // Add appropriate ARIA label
        aria-describedby="video-dialog-description" // Optional: Add description
        sx={{
          background: "transparent",
          "& .MuiDialog-paper": {
            maxWidth: { xs: "-webkit-fill-available", sm: "370px" },
            maxHeight: "100%",
          },
          "@media (max-width: 600px)": {
            "& .MuiDialog-paper": {
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              margin: 0,
            },
          },
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          {completeVideo?.completeVideo?.secureHttpPath ? (
            <>
              <IconButton
                onClick={handleDialogClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  left: 20,
                  color: "#000",
                  background: "#fff",
                  borderRadius: "50%",
                  zIndex: 1012,
                  padding: "4px",
                }}
              >
                <KeyboardBackspaceIcon
                  sx={{
                    fontSize: "1.2rem",
                  }}
                />
              </IconButton>

              <video
                id="myVideo"
                autoPlay
                muted={false}
                loop
                controls={false} // Disable video controls
                onContextMenu={(e) => e.preventDefault()}
                disablePictureInPicture
                controlsList="nodownload noremoteplayback noplaybackrate"
                playsInline
                style={{
                  width: "auto",
                  height: "100%",
                  objectFit: isSmallScreen ? "cover" : "contain",
                }}
              >
                <source
                  src={completeVideo?.completeVideo?.secureHttpPath}
                  type="video/mp4"
                />
              </video>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "-webkit-fill-available",
                  padding: "0px 8px",
                }}
              >
                <VideoServices completeVideo={completeVideo} cid={cid} />
              </Box>
            </>
          ) : (
            <Box sx={{ margin: "auto", position: "absolute" }}>
              <CircularProgress color="success" />
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default VideoSlider;
